import React from "react";

/**
 * Global error boundary — degrades gracefully AND surfaces the real
 * diagnostic (error name, message, offending component from the
 * componentStack) so we don't chase phantom bugs. The debug details
 * panel is expanded by default in preview / development builds and
 * collapsed on production hostnames.
 */
export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[GlobalErrorBoundary]", error, info?.componentStack);
    this.setState({ componentStack: info?.componentStack || null });
  }

  handleReset = () => this.setState({ hasError: false, error: null, componentStack: null });

  // Extract the first offending component name from React's componentStack.
  offendingComponent() {
    const stack = this.state.componentStack || "";
    const match = stack.match(/\s+at\s+(\w+)/);
    return match ? match[1] : "unknown";
  }

  isDebugContext() {
    if (typeof window === "undefined") return false;
    const h = window.location.hostname;
    return (
      h === "localhost" ||
      h === "127.0.0.1" ||
      h.endsWith(".preview.emergentagent.com") ||
      h.endsWith(".emergent.host")
    );
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const errName = err?.name || "Error";
    const errMsg  = err?.message || String(err);
    const offender = this.offendingComponent();
    const debug = this.isDebugContext();

    return (
      <div
        role="alert"
        data-testid="global-error-boundary"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: "transparent",
          color: "#F2F1EE",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 720,
            background: "#2A2A2E",
            border: "1px solid rgba(184,150,90,0.18)",
            padding: 28,
            borderRadius: 6,
          }}
        >
          <div
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 24,
              color: "#B8965A",
              marginBottom: 8,
            }}
          >
            Something went sideways
          </div>
          <p
            data-testid="error-boundary-summary"
            style={{ fontSize: 13, color: "#B5B5BD", lineHeight: 1.6, marginBottom: 14 }}
          >
            <b>{offender}</b> · {errName}: <span style={{ color: "#E5E5EA" }}>{errMsg}</span>
          </p>
          <details
            open={debug}
            data-testid="error-boundary-details"
            style={{ marginBottom: 14, color: "#B5B5BD", fontSize: 12 }}
          >
            <summary style={{ cursor: "pointer", color: "#B8965A" }}>
              Debug details {debug ? "(preview build)" : ""}
            </summary>
            <pre
              data-testid="error-boundary-stack"
              style={{
                marginTop: 8,
                padding: 12,
                background: "#1A1A1C",
                border: "1px solid #3a3a3f",
                borderRadius: 4,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, Menlo, monospace",
                fontSize: 11,
                lineHeight: 1.5,
                maxHeight: 260,
                overflow: "auto",
              }}
            >
{`${errName}: ${errMsg}
${err?.stack ? "\n" + err.stack : ""}
${this.state.componentStack ? "\ncomponentStack:" + this.state.componentStack : ""}`}
            </pre>
          </details>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={this.handleReset}
              data-testid="error-boundary-retry"
              style={{
                background: "#B8965A",
                color: "#1A1A1C",
                border: "none",
                padding: "8px 16px",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                cursor: "pointer",
                borderRadius: 2,
              }}
            >
              Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="error-boundary-reload"
              style={{
                background: "transparent",
                color: "#F2F1EE",
                border: "1px solid #444",
                padding: "8px 16px",
                fontSize: 12,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                cursor: "pointer",
                borderRadius: 2,
              }}
            >
              Hard Refresh
            </button>
          </div>
        </div>
      </div>
    );
  }
}
